import React from 'react';

interface SkeletonProps {
  className?: string;
}

/**
 * Base Skeleton component for loading states
 * Provides a shimmering animation effect
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-neutral-200 via-neutral-300 to-neutral-200 bg-[length:200%_100%] animate-shimmer rounded ${className}`}
      style={{
        animation: 'shimmer 1.5s infinite',
      }}
    />
  );
};

/**
 * Skeleton text line - mimics text content
 */
export const SkeletonText: React.FC<{ width?: string; className?: string }> = ({
  width = '100%',
  className = ''
}) => {
  return <Skeleton className={`h-4 ${className}`} style={{ width }} />;
};

/**
 * Skeleton heading - mimics title/headings
 */
export const SkeletonHeading: React.FC<{ width?: string; className?: string }> = ({
  width = '60%',
  className = ''
}) => {
  return <Skeleton className={`h-6 ${className}`} style={{ width }} />;
};

/**
 * Skeleton button - mimics interactive elements
 */
export const SkeletonButton: React.FC<{ width?: string; className?: string }> = ({
  width = '120px',
  className = ''
}) => {
  return <Skeleton className={`h-10 rounded-lg ${className}`} style={{ width }} />;
};

/**
 * Skeleton avatar - circular placeholder for user images
 */
export const SkeletonAvatar: React.FC<{ size?: number; className?: string }> = ({
  size = 40,
  className = ''
}) => {
  return (
    <Skeleton
      className={`rounded-full ${className}`}
      style={{ width: size, height: size }}
    />
  );
};

/**
 * Skeleton card - mimics card container content
 */
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-xl border border-neutral-200 p-6 ${className}`}>
      <div className="space-y-3">
        <SkeletonHeading width="40%" />
        <SkeletonText width="100%" />
        <SkeletonText width="80%" />
      </div>
    </div>
  );
};

/**
 * Skeleton table row - mimics table data
 */
export const SkeletonTableRow: React.FC<{ columns?: number }> = ({ columns = 4 }) => {
  return (
    <div className="flex gap-4 py-3 border-b border-neutral-200">
      {Array.from({ length: columns }).map((_, i) => (
        <SkeletonText key={i} width={i === 0 ? '30%' : '20%'} className="flex-1" />
      ))}
    </div>
  );
};

/**
 * Skeleton metric card - mimics stats/metric cards
 */
export const SkeletonMetric: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-xl border border-neutral-200 p-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="p-2.5 rounded-lg" style={{ width: 48, height: 48 }} />
          <div className="space-y-2">
            <SkeletonText width="100px" />
            <SkeletonHeading width="80px" />
          </div>
        </div>
        <Skeleton className="h-6 w-16 rounded" />
      </div>
    </div>
  );
};

/**
 * Skeleton chart - mimics chart visualization
 */
export const SkeletonChart: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-xl border border-neutral-200 p-6 ${className}`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonHeading width="150px" />
            <SkeletonText width="200px" />
          </div>
          <SkeletonButton width="100px" />
        </div>
        <div className="h-64 flex items-end gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton
              key={i}
              className="flex-1 rounded-t"
              style={{
                height: `${40 + Math.random() * 60}%`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
